import React, { useMemo } from 'react';
import { CheckCircle2, AlertTriangle, Ghost } from 'lucide-react';
import { getVisualCategoryCoverage, getTypeCategoryCoverage, type CategoryCoverageReport } from '../../content/questionBank/registryExplorer/categoryCoverage';
/**
 * Surfaces drift between the runtime registries (`VISUAL_REGISTRY`,
 * `QUESTION_TYPE_REGISTRY`) and the admin explorer's hard-coded category
 * mappings (`VISUAL_CATEGORIES`, `TYPE_CATEGORIES`).
 *
 * Implements the governance-check half of ADMIN_DOMAIN_RC_AUDIT_2026 §19.2.2 /
 * P1-2. The UI bucket "Overige Actieve…" already absorbs uncategorised items
 * in the explorer, but a designer/dev needed an at-a-glance signal that the
 * mapping is out of sync so new registry entries get properly placed.
 */
export function CategoryCoverageWidget() {
  const visualReport = useMemo(() => getVisualCategoryCoverage(), []);
  const typeReport = useMemo(() => getTypeCategoryCoverage(), []);
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7801" data-mp="muafpmlvv5pv">
      <div className="px-4 sm:px-5 py-4 border-b border-gray-100 bg-gray-50" data-id="element-7802" data-mp="262qhaw2m9h64">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-7803" data-mp="215qceohi6kgl">
          Registry-categorie dekking
        </h3>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-7804" data-mp="1wc5o5o5pp4ul">
          Controleert of elk geregistreerd item in een explorer-categorie zit
        </p>
      </div>
      <div className="divide-y divide-gray-100" data-id="element-7805" data-mp="1ncqukfu90e5v">
        <CoverageRow label="Visuals" report={visualReport} data-id="element-7806" data-mp="1nebs8ebv0d7y" />
        <CoverageRow label="Vraagtypen" report={typeReport} data-id="element-7807" data-mp="1ido2dvzda2fb" />
      </div>
    </div>;
}
interface CoverageRowProps {
  label: string;
  report: CategoryCoverageReport;
}
function CoverageRow({
  label,
  report
}: CoverageRowProps) {
  const isClean = report.uncategorised.length === 0 && report.phantom.length === 0;
  return <div className="px-4 sm:px-5 py-4" data-id="element-7808" data-mp="37rkfocf5j4ib">
      <div className="flex items-start justify-between gap-3" data-id="element-7809" data-mp="33radzbye7099">
        <div className="flex items-center gap-2" data-id="element-7810" data-mp="33rs5jva40qau">
          {isClean ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-7811" data-mp="113k5yw5sj03r" /> : <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-7812" data-mp="15vlhugz1qot9" />}
          <div data-id="element-7813" data-mp="s4gjrjbbu4l1">
            <div className="text-sm font-medium text-gray-900" data-id="element-7814" data-mp="s4yeucm96crd">{label}</div>
            <div className="text-xs text-gray-500" data-id="element-7815" data-mp="w50kewv3zqbx">
              {report.categorisedCount} van {report.registrySize} geregistreerde
              items gekoppeld aan een categorie
            </div>
          </div>
        </div>
        {isClean && <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-7816" data-mp="1otefp4utc8js">
            In sync
          </span>}
      </div>

      {report.uncategorised.length > 0 && <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-7817" data-mp="3vkdx214sbsnl">
          <div className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1.5" data-id="element-7818" data-mp="4e2oljxg3pm8">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-7819" data-mp="3s2g8m047jr0e" />
            {report.uncategorised.length} ongekategoriseerd
            {report.uncategorised.length === 1 ? '' : 'e'} item
            {report.uncategorised.length === 1 ? '' : 's'}
          </div>
          <p className="text-[11px] text-amber-800 mb-2" data-id="element-7820" data-mp="5uqcggpe9y3u">
            Deze items staan wel in de registry maar zijn nog niet toegewezen
            aan een categorie in <code data-id="element-7821" data-mp="39bowwrwqzoiu">categoryDefinitions.ts</code>.
          </p>
          <div className="flex flex-wrap gap-1" data-id="element-7822" data-mp="399vs7i5p3edh">
            {report.uncategorised.map(id => <code key={id} className="text-[10px] font-mono bg-white border border-amber-200 text-amber-800 px-1.5 py-0.5 rounded" data-id="element-7823" data-mp="34bapnc2z0ilv">
                {id}
              </code>)}
          </div>
        </div>}

      {report.phantom.length > 0 && <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3" data-id="element-7824" data-mp="2lmnm4y0g01ae">
          <div className="text-xs font-semibold text-rose-900 mb-1.5 flex items-center gap-1.5" data-id="element-7825" data-mp="2lkji8g2jn2a4">
            <Ghost className="w-3.5 h-3.5" data-id="element-7826" data-mp="1ot64apb1yrh1" />
            {report.phantom.length} verweesde categorie-verwijzing
            {report.phantom.length === 1 ? '' : 'en'}
          </div>
          <p className="text-[11px] text-rose-800 mb-2" data-id="element-7827" data-mp="1xudwqqqw0y37">
            Deze category-mapping verwijst naar items die niet (meer) in de
            registry voorkomen — hernoemd of verwijderd zonder de mapping bij te
            werken.
          </p>
          <ul className="space-y-0.5" data-id="element-7828" data-mp="2cklbavdrxwym">
            {report.phantom.map(p => <li key={`${p.categoryId}:${p.itemId}`} className="text-[11px] font-mono text-rose-800" data-id="element-7829" data-mp="2hjy5k16z66b8">
                <span className="text-rose-500" data-id="element-7830" data-mp="2lk8cph6i72ro">{p.categoryId}</span> →{' '}
                {p.itemId}
              </li>)}
          </ul>
        </div>}
    </div>;
}